<template>
  <v-container id="product-catalog-drawer">
    <div class="pl-6 pr-2 py-2">
      <h3 class="pr-3 my-2 d-flex justify-space-between">
        {{ drawerHeader.title }}
        <v-btn
          id="close-feed-drawer"
          v-track="'close-feed-drawer'"
          icon
          small
          class="ma-0 pa-0"
          @click.stop="$store.commit('ui/drawer.right.toggle')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </h3>
      <v-container class="pl-0">
        <div
          v-if="drawerHeader.description"
          class="body-2 mb-7"
        >
          {{ drawerHeader.description }}
        </div>
        <v-card
          outlined
          color="#FFF2CC"
          class="pa-2 mb-2 mt-n2 d-flex"
          max-width="680px"
        >
          <v-icon class="mr-2">
            mdi-information-outline
          </v-icon>
          <div class="subtitle-2">
            You are about to edit your product catalog. If you make any changes,
            then be aware that any edits you make will be overwritten at the next catalog sync.
          </div>
        </v-card>
        <v-form
          ref="productForm"
          v-model="valid"
          lazy-validation
        >
          <v-row>
            <v-col class="pb-1">
              Item ID <span class="pl-4">{{ product.item_id }}</span>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-0">
              Source
              <span class="pl-4 text-capitalize">
                {{ (sourceType === 'commerce') ? `${sourceType} Cloud` : sourceType | underscoreRemover }}
              </span>
            </v-col>
          </v-row>
          <v-row class="mt-3">
            <v-col>
              <v-text-field
                id="product-name"
                v-model.trim="product.name"
                label="Name *"
                :rules="[
                  v => !!v || 'Name is required',
                  v => !v || !!v.trim() || 'Name cannot be blank!',
                ]"
                outlined
                required
                :error-messages="errorBag.name"
                persistent-hint
                @input="errorBag.name = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                id="product-price"
                v-model="product.price"
                label="Price *"
                :rules="[
                  v => v === 0 || !!v || 'Price is required',
                  v => !v || /^[+]?([0-9]*[.])?[0-9]+$/.test(v) || 'Only numbers and decimal is allowed!',
                ]"
                outlined
                required
                :error-messages="errorBag.price"
                persistent-hint
                @input="errorBag.price = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                id="product-image-url"
                v-model.trim="product.image_url"
                label="Image URL"
                outlined
                required
                :error-messages="errorBag.image_url"
                persistent-hint
                @input="errorBag.image_url = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                id="product-store-url"
                v-model.trim="product.store_url"
                label="Store URL *"
                :rules="[
                  v => !!v || 'Store URL is required',
                  v => !v || !!v.trim() || 'Store URL cannot be blank!',
                  v => !v || URLTest(v) || 'Invalid store url!',
                ]"
                outlined
                required
                :error-messages="errorBag.store_url"
                persistent-hint
                @input="errorBag.store_url = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              class="d-flex selection-bg-white pb-1"
            >
              <div class="category-selector-wrapper">
                <div
                  class="category-selector mt-n2 ml-2 px-1"
                  :class="{'d-none' : (!categoryFocusFlag && (product.categories && product.categories.length == 0))}"
                >
                  <label class="v-label--active theme--light">
                    Category
                  </label>
                </div>
              </div>
              <treeselect
                id="product-category-2"
                v-model="product.categories"
                v-track="'product-category'"
                :multiple="true"
                :options="categories"
                value-consists-of="ALL"
                :show-count="true"
                show-count-of="ALL_DESCENDANTS"
                :close-on-select="true"
                :default-expand-level="1"
                :limit="10"
                :normalizer="normalizer"
                value-format="object"
                no-results-text="Category not found"
                no-options-text="No Categories are available for the Source"
                :placeholder="(!categoryFocusFlag || (product.categories && product.categories.length > 0)) ? 'Category' : ''"
                class="pr-2 background-white"
                @open="onOpenTags()"
                @close="onCloseTags()"
              />
            </v-col>
          </v-row>
          <div class="ml-3 mb-4 caption font-weight-regular">
              Choose a category from the list.
          </div>
          <v-row>
            <v-col>
              <v-textarea
                id="product-description"
                v-model.trim="product.description"
                :rules="[v => (v ? v.length : 0) <= 255 || 'Description can have upto 255 characters.']"
                maxlength="256"
                label="Description"
                outlined
                no-resize
              />
            </v-col>
          </v-row>
          <v-divider />
          <v-row>
            <v-col cols="12 pl-0 mt-2">
              <v-btn
                id="product-drawer-cancel"
                v-track="'product-drawer-cancel'"
                outlined
                class="ml-3"
                @click.stop="$store.commit('ui/drawer.right.toggle')"
              >
                cancel
              </v-btn>
              <v-btn
                id="product-submit"
                v-track="'product-submit'"
                :dark="valid"
                :disabled="!valid"
                class="ml-3"
                @click="onSubmit()"
              >
                {{ id ? 'Save' : 'Create' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </div>
    <v-overlay :value="loader">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
  </v-container>
</template>
<script>
  import { $categories, $products } from '@/services/'
  import Treeselect from '@riophae/vue-treeselect'
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'

  export default {
    data () {
      return {
        valid: false,
        errorBag: {
          name: '',
          price: '',
          image_url: '',
          store_url: '',
        },
        categories: [],
        loader: false,
        product: {
          item_id: '',
          name: '',
          price: 0,
          image_url: '',
          store_url: '',
          description: '',
        },
        normalizer (node) {
          let childrenNode = ''
          if (node.children) {
            if (node.children.length) {
              childrenNode = node.children
            }
          }
          return {
            id: node.id,
            label: node.name,
            children: childrenNode,
          }
        },
        sourceType: '',
        categoryFocusFlag: false
      }
    },
    components: {
      Treeselect
    },
    computed: {
      // Handle drawer header area according to case!
      drawerHeader () {
        switch (this.action) {
          case 'edit' :
            return {
              title: 'Edit Product',
              description: '',
            }
          default:
            return {
              title: '',
              description: '',
            }
        }
      },
      // Receive user action
      action: {
        get () { return this.$store.state.ui.drawer.right.action },
        set (value) { this.$store.state.ui.drawer.right.action = value },
      },
      id: {
        get () { return this.$store.state.ui.drawer.right.id },
        set (value) { this.$store.state.ui.drawer.right.id = value },
      },
    },
    beforeMount () { this.initData() },
    methods: {
      onOpenTags () {
        this.categoryFocusFlag = true
      },
      onCloseTags () {
        this.categoryFocusFlag = (this.product.categories > 0)
      },
      /**
       * @description Component initial data injection
       * according to cases
       */
      initData () {
        this.loader = true
        this.categoriesLoader = true
        switch (this.action) {
          case 'edit':
            $products.show(this.id).then(response => {
              const result = response.data.data.product
              this.product = result
              this.$refs.productForm.validate()
            }).finally(() => {
              this.loader = false
              this.sourceType = this.product.source
              const payload = {
                source_type: this.sourceType,
                all_record: true,
              }
              $categories.fetchHierarchy(payload).then(response => {
                this.categories = response.data.categories
                this.categoriesLoader = false
              })
            })
            break
          default:
            // default case
            this.categoriesLoader = false
            this.loader = false
        }
      },
      /**
       * Handle all type of form submission
       */
      onSubmit () {
        /**
         * @description Init product category case!
         * Save whatever user type in v-combobox search bar as category
         */
        // Validate before submitting
        if (this.$refs.productForm.validate() && this.valid) {
          this.loader = true
          this.product.category_ids = this.product.categories.map(category => category.id)
          const payload = { product: { ...this.product } }
          // If edit submission
          if (this.id) {
            $products.update(this.id, payload).then(response => {
              this.$root.reloadProducts()
              this.$store.commit('ui/drawer.right.toggle')
              this.$store.commit('ui/snackbar', {
                message: 'Product changes were successfully saved.',
                type: 'success',
              })
            }).catch((error) => {
              this.errorAlert(error.response.data.errors)
            }).finally(() => {
              this.loader = false
            })
          }
        } else {
          this.scrollOnFirstError()
        }
      },
      /**
       * @description used for mange the error bag with key type
       * to inject in UI
       */
      errorAlert (errors) {
        for (const error in errors) {
          this.errorBag[error] = `${error[0].toUpperCase() + error.substring(1).replace(/_/g, ' ')} ${errors[error]}`
        }
        this.scrollOnFirstError()
      },
      /**
       * @description Scroll the view on first validate error
       */
      scrollOnFirstError () {
        this.$nextTick(() => {
          const el = document.querySelector('.v-messages.error--text:first-of-type')
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      },
      /**
       * @description used for validate URL with regex
       */
      URLTest (url) {
        return /^[(http(s)?):\/\/(www\.)?a-zA-Z0-9-@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/.test(url)
      },
    },
  }
</script>
<style lang="scss" scoped>
#product-catalog-drawer {
  .create-category {
    height: 36px;
    background-color: #1e2533;
    color: #ffffff !important;
  }
}
.category-selector {
  position: absolute;
  background-color: white;
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  z-index: 1;
  width: 59px;
}
.category-selector-wrapper {
  position: relative;
}
</style>
